import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Timestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import {
  Clock,
  Eye,
  FileText,
  Loader,
  PenLine,
  RotateCcw,
  Save,
  Smartphone,
} from 'lucide-react';
import { AppInfoService } from '../../services/appInfoService';
import AboutUsEditor from './AboutUsEditor';
import './aboutUsEditor.css';

/**
 * TipTap represents an empty document as "<p></p>", which is not the same as an
 * empty string, so emptiness is judged on the visible text.
 */
const toPlainText = (html: string): string =>
  html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim();

const isBlankHtml = (html: string): boolean => toPlainText(html).length === 0;

const formatTimestamp = (timestamp: Timestamp | null): string => {
  if (!timestamp) return '—';

  return timestamp.toDate().toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const AboutUsSection = () => {
  // The content as stored in Firestore, kept apart from `html` so unsaved edits
  // can be detected and discarded.
  const [savedHtml, setSavedHtml] = useState('');
  const [html, setHtml] = useState('');
  const [modifiedAt, setModifiedAt] = useState<Timestamp | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  // Bumped whenever the body is replaced from outside the editor — loading and
  // discarding both leave the editor showing text that is no longer current.
  const [editorSeed, setEditorSeed] = useState(0);

  const loadAppInfo = useCallback(async () => {
    try {
      setIsLoading(true);
      const appInfo = await AppInfoService.getAppInfo();
      setSavedHtml(appInfo.info);
      setHtml(appInfo.info);
      setModifiedAt(appInfo.modifiedAt);
      setEditorSeed((seed) => seed + 1);
    } catch (error) {
      console.error('Error loading about us content:', error);
      toast.error('Failed to load About Us content', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAppInfo();
  }, [loadAppInfo]);

  const hasChanges = html !== savedHtml;
  const canSave = hasChanges && !isBlankHtml(html);

  const wordCount = useMemo(() => {
    const text = toPlainText(html);
    return text.length === 0 ? 0 : text.split(/\s+/).length;
  }, [html]);

  const handleDiscardChanges = () => {
    setHtml(savedHtml);
    setShowPreview(false);
    setEditorSeed((seed) => seed + 1);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const saved = await AppInfoService.saveAppInfo(html);
      setSavedHtml(saved.info);
      setModifiedAt(saved.modifiedAt);

      toast.success('About Us content saved', {
        position: 'top-right',
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Error saving about us content:', error);
      toast.error(
        error instanceof Error ? error.message : 'Failed to save About Us content',
        { position: 'top-right', autoClose: 5000 }
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader className="size-6 animate-spin text-custom-500" />
        <span className="ml-2 text-slate-500">Loading About Us content...</span>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h6 className="text-15 font-semibold">About Us</h6>
          <p className="text-sm text-slate-500 dark:text-zink-400 mt-1">
            The text shown on the app&apos;s About Us screen.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {hasChanges && (
            <span className="px-2 py-1 text-[11px] rounded-full border border-orange-200 bg-orange-50 text-orange-500 dark:bg-orange-500/10 dark:border-orange-500/30">
              Unsaved changes
            </span>
          )}

          <button
            onClick={() => setShowPreview((previous) => !previous)}
            className="px-3 py-2 text-sm border border-slate-300 dark:border-zink-500 rounded-md hover:bg-slate-50 dark:hover:bg-zink-600 transition-colors flex items-center"
          >
            {showPreview ? (
              <>
                <PenLine className="size-4 mr-2" />
                Edit
              </>
            ) : (
              <>
                <Eye className="size-4 mr-2" />
                Preview
              </>
            )}
          </button>

          {hasChanges && (
            <button
              onClick={handleDiscardChanges}
              disabled={isSaving}
              className="px-3 py-2 text-sm border border-slate-300 dark:border-zink-500 rounded-md hover:bg-slate-50 dark:hover:bg-zink-600 transition-colors flex items-center disabled:opacity-50"
            >
              <RotateCcw className="size-4 mr-2" />
              Discard
            </button>
          )}

          <button
            onClick={handleSave}
            disabled={!canSave || isSaving}
            title={
              canSave
                ? 'Overwrite the About Us content shown in the app'
                : 'Make a change to save'
            }
            className="px-4 py-2 text-sm bg-custom-500 hover:bg-custom-600 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isSaving ? (
              <>
                <Loader className="size-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="size-4 mr-2" />
                Save
              </>
            )}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="xl:col-span-2">
          {/* The editor is seeded from `html` rather than from the stored doc,
              so edits made before flipping to Preview survive the unmount that
              toggling causes. */}
          {showPreview ? (
            // Mirrors the editor's shape — bordered box, header strip, then a
            // body of the same fixed height — so toggling does not resize the
            // column.
            <div className="border border-slate-200 dark:border-zink-500 rounded-md overflow-hidden">
              <div className="flex items-center p-2 border-b border-slate-200 dark:border-zink-500">
                <span className="px-2 py-1.5 text-sm text-slate-500 dark:text-zink-400 flex items-center">
                  <Eye className="size-4 mr-2" />
                  Preview — as the app renders it
                </span>
              </div>
              <div className="h-[420px] overflow-y-auto px-4 py-3">
                {isBlankHtml(html) ? (
                  <div className="flex flex-col items-center justify-center h-full text-slate-400">
                    <FileText className="size-10 mb-2" />
                    <p className="text-sm">Nothing to preview yet</p>
                  </div>
                ) : (
                  <div
                    className="about-rich-text"
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                )}
              </div>
            </div>
          ) : (
            <AboutUsEditor
              content={html}
              contentKey={`about-${editorSeed}`}
              onChange={setHtml}
              disabled={isSaving}
            />
          )}

          <p className="text-xs text-slate-500 dark:text-zink-400 mt-2">
            Formatting is limited to what the mobile app renders: headings,
            paragraphs, bold, italic and lists.
          </p>
        </div>

        <div>
          <div className="p-4 rounded-md border border-slate-200 dark:border-zink-500">
            <h6 className="text-sm font-semibold mb-3">Details</h6>

            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-xs text-slate-500 dark:text-zink-400 flex items-center mb-0.5">
                  <Clock className="size-3 mr-1.5 shrink-0" />
                  Last updated
                </dt>
                <dd className="text-slate-800 dark:text-zink-100">
                  {formatTimestamp(modifiedAt)}
                </dd>
              </div>

              <div>
                <dt className="text-xs text-slate-500 dark:text-zink-400 flex items-center mb-0.5">
                  <FileText className="size-3 mr-1.5 shrink-0" />
                  Length
                </dt>
                <dd className="text-slate-800 dark:text-zink-100">
                  {wordCount} word{wordCount === 1 ? '' : 's'}
                </dd>
              </div>

              <div>
                <dt className="text-xs text-slate-500 dark:text-zink-400 flex items-center mb-0.5">
                  <Smartphone className="size-3 mr-1.5 shrink-0" />
                  Shown in app
                </dt>
                <dd className="text-slate-800 dark:text-zink-100">
                  Settings → About Us
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default AboutUsSection;
